import type { Locale, TranslationTree } from "@llmgateway/i18n";

export const adminMessages = {
	en: {
		admin: {
			auth: {
				emailInvalid: "Please enter a valid email address",
				loginSuccessful: "Login successful",
				or: "Or",
				passwordMin: "Password must be at least 6 characters",
				passwordRequired: "Password is required",
				signingIn: "Signing in...",
				subtitle: "Enter your email and password to sign in to your account",
				unknownError: "An unknown error occurred",
				welcomeBack: "Welcome back",
			},
			common: {
				actions: "Actions",
				all: "All",
				allModels: "All Models",
				allProviders: "All Providers",
				allTime: "All-time",
				amount: "Amount",
				anonymous: "Anonymous",
				archive: "Archive",
				archived: "Archived",
				avgTtft: "Avg TTFT",
				avgDuration: "Avg Duration",
				backToOrganization: "Back to Organization",
				baseUrl: "Base URL",
				cached: "Cached",
				cacheWrite: "Cache Write",
				cancel: "Cancel",
				client: "Client",
				completed: "Completed",
				contentFilter: "Content Filter",
				context: "Context",
				cost: "Cost",
				country: "Country",
				created: "Created",
				credits: "Credits",
				date: "Date",
				delete: "Delete",
				deleting: "Deleting...",
				details: "Details",
				disabled: "Disabled",
				discount: "Discount",
				description: "Description",
				email: "Email",
				errorRate: "Error Rate",
				errors: "Errors",
				event: "Event",
				expires: "Expires",
				family: "Family",
				failed: "Failed",
				free: "Free",
				gateway: "Gateway",
				failedCopyId: "Failed to copy ID",
				hide: "Hide",
				history: "History",
				id: "ID",
				idCopied: "ID copied",
				inputPrice: "Input Price",
				inputTokens: "Input Tokens",
				joined: "Joined",
				lastUpdated: "Last Updated",
				lengthLimit: "Length Limit",
				limit: "Limit",
				loading: "Loading...",
				maxOutput: "Max Output",
				message: "Message",
				model: "Model",
				models: "Models",
				name: "Name",
				never: "Never",
				noData: "No data.",
				notProvided: "Not provided",
				none: "None",
				organization: "Organization",
				outputPrice: "Output Price",
				outputTokens: "Output Tokens",
				pageOf: "Page {page} of {totalPages}",
				payments: "Payments",
				plan: "Plan",
				premiumWeek: "Premium (week)",
				project: "Project",
				previous: "Previous",
				next: "Next",
				pricing: "Pricing",
				provider: "Provider",
				providers: "Providers",
				reason: "Reason",
				region: "Region",
				requests: "Requests",
				role: "Role",
				save: "Save",
				search: "Search",
				showing: "Showing {from} to {to} of {total}",
				size: "Size",
				source: "Source",
				status: "Status",
				streaming: "Streaming",
				token: "Token",
				tokens: "Tokens",
				toolCalls: "Tool Calls",
				totalCost: "Total Cost",
				totalRequests: "Total Requests",
				totalTokens: "Total Tokens",
				type: "Type",
				unknown: "unknown",
				unarchive: "Unarchive",
				upstream: "Upstream",
				usage: "Usage",
				userAgent: "User Agent",
				value: "Value",
				verified: "Verified",
				unverified: "Unverified",
			},
			dashboard: {
				audience: "Audience",
				creditFlow: "Credit Flow",
				description: "Overview of users, customers, and revenue.",
				fees: "Fees",
				gifted: "Gifted",
				allTimeCreditsPurchased: "All-time credits purchased",
				liveData: "Live data",
				netRevenueSubtitle: "Net (excl. Stripe fees & refunds)",
				orgs: "Orgs",
				overage: "Overage: {amount}",
				overageDescription: "Spending exceeding topped-up credits.",
				paying: "Paying",
				processed: "Processed",
				refunds: "Refunds",
				revenue: "Revenue",
				spent: "Spent",
				title: "Admin Dashboard",
				totalSignups: "Total sign-ups",
				unused: "Unused",
				verified: "Verified",
				viewOrganizations: "View Organizations",
			},
			globalStats: {
				allDimensionAcrossRange: "All {count} {dimension} across {range}.",
				breakdown: "Breakdown",
				byModel: "By model",
				bySource: "By x-source",
				canonical: "Canonical",
				canonicalModel: "canonical model",
				costBreakdown: "Input: {input} · Output: {output}",
				dailyTimeseries: "Daily timeseries",
				description:
					"Cross-organization usage aggregated by day, grouped by model or x-source header.",
				distinctDimension: "Distinct {dimension}",
				errorsCached: "Errors: {errors} · Cached: {cached}",
				failedToLoad: "Failed to load stats.",
				mapping: "mapping",
				mappings: "Mappings",
				metricShare: "{metric} share - {dimension}",
				noDataForRange: "No data for this range.",
				ofTotal: "of {total}",
				otherCount: "Other ({count})",
				pageSlash: "Page {page} / {totalPages}",
				selectedRange: "selected range",
				sources: "sources",
				timeseriesAggregateDescription:
					"Aggregate {metric} per day across all {dimensions}.",
				timeseriesBreakdownDescription:
					"Aggregate {metric} per day broken down by {dimension}.",
				title: "Global Stats",
				tokensBreakdown: "In: {input} · Cached: {cached} · Out: {output}",
				topOtherAcrossRange: "Top 10 + Other across {range}.",
			},
			history: {
				aggregatedProviderRequestVolumeDescription:
					"Aggregated across all providers. Request volume, errors, latency, and tokens over time",
				avgDurationMs: "Avg Duration (ms)",
				avgTtftMs: "Avg TTFT (ms)",
				costUsd: "Cost ($)",
				noProvidersServeModel: "No providers serve this model",
				perProviderHistory: "Per-Provider History",
				projectRequestVolumeDescription:
					"Hourly request volume, errors, tokens, and cost for this project",
				requestVolumeForProvider:
					"Request volume, errors, latency, and tokens for {provider}",
				requestVolumeDescription:
					"Request volume, errors, latency, and tokens over time",
				tokensPerSecond: "t/s",
			},
			language: {
				label: "Language",
			},
			nav: {
				chatPlans: "Chat Plans",
				chatSupportLogs: "Chat Support Logs",
				contactSubmissions: "Contact Submissions",
				dashboard: "Dashboard",
				devpass: "DevPass",
				globalDiscounts: "Global Discounts",
				globalRateLimits: "Global Rate Limits",
				globalStats: "Global Stats",
				main: "Main",
				modelMappings: "Model Mappings",
				models: "Models",
				organizations: "Organizations",
				paymentFailures: "Payment Failures",
				providers: "Providers",
				toggleMenu: "Toggle menu",
			},
			organizations: {
				apiKeys: "API Keys",
				allTimeCredits: "All Time Credits",
				back: "Back",
				blockAccount: "Block account",
				blockAccountConfirm: "Yes, block account",
				blockAccountDescription: "You are about to block {orgName}. This will:",
				blockAccountFailed: "Failed to block organization",
				blockAccountSubscriptionEffect:
					"Immediately cancel every active Stripe subscription on this organization (DevPass and any pro subscription).",
				blockAccountGatewayEffect:
					"Mark the organization as deleted so gateway requests are rejected.",
				blockAccountMembersEffect:
					"Deactivate all members and sign them out of every session.",
				blockAccountReminder:
					"This action is intended for confirmed abuse (duplicate cards, key sharing, fraud). Re-enable via the status toggle if it was done by mistake.",
				blockAccountTitle: "Block this account?",
				collapseIamRules: "Collapse IAM rules",
				createdAt: "Created {date}",
				creditsValue: "Credits: {credits}",
				devPlan: "Dev Plan",
				devPlanValue: "Dev: {plan}",
				disableOrganization: "Disable organization",
				disableOrganizationConfirm:
					'Are you sure you want to disable organization "{orgName}"? All members will be deactivated and signed out, and gateway requests will be rejected with HTTP 410.',
				expandIamRules: "Expand IAM rules",
				giftCredits: "Gift Credits",
				giftCreditsAmount: "Credit Amount",
				giftCreditsComment: "Comment (Optional)",
				giftCreditsCommentHelper: "Stored in the transaction description",
				giftCreditsCommentPlaceholder:
					"e.g. Welcome bonus, Compensation for downtime",
				giftCreditsDescription:
					"Gift credits to {orgName}. This creates a transaction record and updates the organization's credit balance.",
				giftCreditsFailed: "Failed to gift credits",
				giftCreditsInvalid: "Credit amount must be a positive number",
				giftCreditsPlaceholder: "e.g. 50",
				iamRules: "IAM Rules",
				manageDiscounts: "Manage Discounts",
				manageRateLimits: "Manage Rate Limits",
				members: "Members",
				noApiKeysFound: "No API keys found",
				noMembersFound: "No members found",
				noProviderKeysFound: "No provider keys found",
				noTransactionsFound: "No transactions found",
				noResults: "No organizations found",
				providerKeys: "Provider Keys",
				reEnableOrganization: "Re-enable organization",
				reEnableOrganizationConfirm:
					'Are you sure you want to re-enable organization "{orgName}"? Members will be reactivated (unless they belong to another disabled organization) and gateway requests will resume.',
				referralBonus: "Referral Bonus",
				referralBonusDescription:
					"When enabled, organizations referred by {orgName} receive a bonus on their first credit top-up.",
				referralBonusEnable: "Enable bonus",
				referralBonusEnableHelper:
					"Referred users get the bonus only when signing up via this org's referral link.",
				referralBonusFailed: "Failed to update referral bonus",
				referralBonusPercent: "Bonus percent",
				referralBonusPercentHelper:
					"Percentage added to the referred org's first top-up (e.g. 50 = +50% credits).",
				referralBonusPercentInvalid:
					"Percent must be a number between 0 and 1000",
				referralBonusTitle: "Referral Signup Bonus",
				searchPlaceholder: "Search by name, email, member email, or ID...",
				subtitle: "{count} organizations found - Total credits: {credits}",
				title: "Organizations",
				transactions: "Transactions",
				totalSpent: "Total Spent",
			},
			paymentFailures: {
				amount: "Amount",
				declineCode: "Decline Code",
				days: "Days",
				description:
					"Stripe payment declines and failures over the last {days} days",
				failedToLoad: "Failed to load payment failures.",
				last7Days: "Last 7 days",
				last30Days: "Last 30 days",
				message: "Message",
				noResults: "No payment failures found.",
				searchPlaceholder: "Search by email...",
				source: "Source",
				stripe: "Stripe",
				time: "Time",
				title: "Payment Failures",
				topDeclineCodes: "Top decline codes (30d)",
				truncated:
					"Showing 100 of {total} results. Narrow your search or reduce the date range to see more.",
				view: "View",
			},
			charts: {
				costByModel: "Cost by Model",
				costByModelDescription:
					"Top 20 models by cost across all organizations",
				costByModelOrgDescription:
					"Top 20 models by cost for this organization",
				costByModelOverTime: "Cost by Model Over Time",
				costByModelOverTimeOrgDescription:
					"Stacked breakdown of top 10 models over the selected window",
				costByModelOverTimeProjectDescription:
					"Stacked breakdown of top 10 models for this project",
				devpassRevenueUsage: "DevPass revenue & usage",
				chatPlansRevenueUsage: "Chat Plans revenue & usage",
				devpassTimeseriesDescription:
					"Daily revenue from DevPass transactions, real provider cost across current and former subscribers, and the resulting margin. Totals aggregate the selected date range and may not match current-cycle KPI cards.",
				chatPlansTimeseriesDescription:
					"Daily revenue from Chat Plan transactions, real provider cost across current and former subscribers, and the resulting margin. Totals aggregate the selected date range and may not match current-cycle KPI cards.",
				chatPlansTimeseriesFailed: "Failed to load Chat Plans timeseries.",
				providerCost: "Provider cost",
				margin: "Margin",
				net: "Net",
				noDataForWindow: "No data for this time window",
				paidCustomers: "Paid Customers",
				revenueDescription:
					"Cumulative processed, revenue (after fees), and net (after fees & refunds)",
				signups: "Signups",
				signupsDescription: "Cumulative signups and paid customers over time",
				signupsPaidCustomers: "Signups & Paid Customers",
				totalRevenue: "Total Revenue",
			},
			contactSubmissions: {
				archive: "Archive",
				back: "Back to submissions",
				companySize: "Company Size",
				delete: "Delete Submission",
				deleteDescription:
					"This submission will be archived and hidden from the default list.",
				deletePermanentDescription:
					"This will permanently delete this contact submission. This action cannot be undone.",
				details: "Submission Details",
				failedLoad: "Failed to load submission. Please try again later.",
				found: "{count} submissions found",
				generateReply: "Generate reply with AI",
				noResults: "No submissions found",
				notFound: "Submission not found.",
				rejectionReason: "Rejection Reason",
				replySent: "Reply sent successfully",
				searchPlaceholder: "Search by name, email, or message...",
				statusDelivered: "Delivered",
				statusFailed: "Failed",
				statusPending: "Pending",
				statusRejected: "Rejected",
				submittedAt: "Submitted {date}",
				title: "Contact Submissions",
				unarchive: "Unarchive",
			},
			dateRange: {
				allTime: "All time",
				apply: "Apply",
				custom: "Custom",
				customRange: "Custom range...",
				end: "End",
				from: "From",
				last7Days: "Last 7 days",
				last30Days: "Last 30 days",
				last3Months: "Last 3 months",
				last6Months: "Last 6 months",
				last90Days: "Last 90 days",
				last12Months: "Last 12 months",
				lastMonth: "Last month",
				lastWeek: "Last week",
				lastYear: "Last year",
				lastQuarter: "Last quarter ({quarter})",
				start: "Start",
				selectEndMonth: "Select end month",
				thisMonth: "This month",
				thisQuarter: "This quarter ({quarter})",
				thisWeek: "This week",
				thisYear: "This year",
				to: "To",
				today: "Today",
				twoQuartersAgo: "2 quarters ago ({quarter})",
				threeQuartersAgo: "3 quarters ago ({quarter})",
				backToPresets: "Back to presets",
			},
			detailStats: {
				canceled: "Canceled",
				clientErrors: "Client Errors",
				gatewayErrors: "Gateway Errors",
				upstreamErrors: "Upstream Errors",
			},
			discounts: {
				add: "Add Discount",
				backToOrganization: "Back to Organization",
				confirmDelete: "Are you sure you want to delete this discount?",
				create: "Create Discount",
				createDescription:
					"Create a new discount for a provider, model, or combination.",
				customerPays: "Customer pays {percent}% of the original price",
				description: "Discounts that apply to all organizations",
				expiresAtOptional: "Expires At (optional)",
				failedCreate: "Failed to create discount",
				failedCreateDuplicate:
					"Failed to create discount. It may already exist.",
				failedCreateGeneric: "An error occurred while creating the discount",
				howGlobalWorks: "How global discounts work",
				howOrgWorks: "How discounts work",
				howWorksApplied: "They are applied automatically to API usage",
				howWorksAutoOrg:
					"Discounts are applied automatically to all API requests from this organization",
				howWorksCustomerPays:
					"A 30% discount means the customer pays 70% of the original price",
				howWorksGlobalAll: "Global discounts apply to all organizations",
				howWorksGlobalOverride:
					"Global discounts apply to ALL organizations unless overridden by org-specific discounts",
				howWorksOrgPrecedence:
					"Organization discounts take precedence over global discounts",
				howWorksPrecedence:
					"More specific discounts (provider + model) take precedence over broader ones",
				neverExpiresHelper: "Leave empty for a discount that never expires",
				noGlobalResults: "No global discounts configured",
				noGlobalResultsDescription:
					"Global discounts apply to all organizations",
				noOrgResults: "No discounts configured for this organization",
				noOrgResultsDescription:
					"Add a discount to give this organization special pricing",
				off: "{percent} off",
				percentLabel: "Discount Percentage",
				percentInvalid: "Discount must be between 0 and 100",
				percentPlaceholder: "e.g., 30 for 30% off",
				reasonOptional: "Reason (optional)",
				reasonPlaceholder: "e.g., Enterprise partner discount",
				selectProviderOrModel: "Please select at least a provider or a model",
				title: "Global Discounts",
				orgTitle: "Discounts",
			},
			models: {
				noResults: "No models found",
				perProviderHistory: "Per-Provider History",
				providerCount: "{count} provider",
				providersCount: "{count} providers",
				searchPlaceholder: "Search by name or ID...",
				subtitle: "{count} models found - click a row to view details",
				title: "Models",
			},
			modelMappings: {
				bucketedHourly: "Per-project data is bucketed hourly",
				projectSubtitle:
					"{count} model-provider combinations for {projectName}",
				backToProject: "Back to Project",
				noResults: "No mappings found",
				searchPlaceholder: "Search by model or provider...",
				subtitle: "{count} mappings - all models available per provider",
				title: "Model-Provider Mappings",
			},
			providers: {
				noModelsServed: "No models served by this provider",
				noResults: "No providers found",
				subtitle: "{count} providers - click a row to view history",
				title: "Providers",
			},
			projectLogs: {
				allProviders: "All providers",
				allSources: "All sources",
				allStatuses: "All statuses",
				filterByModel: "Filter by model",
				filterByProvider: "Filter by provider",
				filterBySource: "Filter by source",
				filterByStatus: "Filter by status",
				loadMore: "Load More",
				loadingLogs: "Loading logs...",
				noLogs: "No logs found for this project.",
				noModels: "No models found.",
				recentLogs: "Recent Logs",
				refresh: "Refresh",
				refreshing: "Refreshing...",
				searchModels: "Search models...",
			},
			projects: {
				cachingEnabled: "caching enabled",
				cached: "cached",
				modelProviderMappings: "Model-Provider Mappings",
				projects: "Projects",
			},
			rateLimits: {
				add: "Add Rate Limit",
				confirmDelete: "Are you sure you want to delete this rate limit?",
				create: "Create Rate Limit",
				description: "RPM caps that apply to all organizations",
				dialogDescription:
					"Set a maximum requests per minute or per day cap for a provider, model, or combination.",
				enforcement: "Enforcement",
				enforcementGlobal: "Global (shared)",
				enforcementGlobalDescription:
					"All organizations share a single counter against this limit",
				enforcementGlobalLong: "Global (shared across all orgs)",
				enforcementPerOrg: "Per-organization",
				enforcementPerOrgDescription:
					"Each organization gets its own counter against this limit",
				failedCreate: "Failed to create rate limit",
				failedCreateDuplicate:
					"Failed to create rate limit. It may already exist.",
				failedCreateGeneric: "An error occurred while creating the rate limit",
				howItWorks: "How global rate limits work",
				howItWorksAll:
					"Global rate limits apply to ALL organizations unless overridden by org-specific rate limits",
				howItWorksCaps:
					"Caps can be defined as requests per minute (RPM) or per day (RPD)",
				howItWorksFallback:
					"When a cap is hit, the gateway prefers other eligible providers before returning 429",
				howItWorksGlobal:
					"Global (shared) enforcement uses a single counter across all orgs combined (e.g. 10 RPM total platform-wide)",
				howItWorksPerOrg:
					"Per-organization enforcement gives each org its own counter (e.g. 10 RPM per org)",
				howItWorksSpecific:
					"More specific rate limits (provider + model) take precedence over broader ones",
				limitType: "Limit Type",
				maxLimit: "Max {limitType}",
				maxLimitInvalid: "Max {limitType} must be a positive integer",
				maxRpdDescription: "Maximum requests per day allowed",
				maxRpmDescription: "Maximum requests per minute allowed",
				modelHelper: "Showing models available for {provider}",
				noResults: "No global rate limits configured",
				noResultsDescription:
					"Global rate limits cap the maximum requests per minute or per day for specific providers/models",
				reason: "Reason",
				reasonOptional: "Reason (optional)",
				reasonPlaceholder: "e.g., Prevent abuse on expensive model",
				selectProviderOrModel: "Please select at least a provider or a model",
				title: "Global Rate Limits",
				orgAddDescription: "Add an RPM or RPD cap for this organization",
				orgHowItWorks: "How rate limits work",
				orgHowWorksFallback:
					"When a rate limit is hit, the gateway falls back to other providers when possible; otherwise it returns 429 Too Many Requests",
				orgHowWorksPrecedence:
					"Organization rate limits take precedence over global rate limits",
				orgHowWorksScope:
					"Rate limits cap the maximum requests per minute (RPM) or per day (RPD) for matching providers and models",
				orgNoResults: "No rate limits configured for this organization",
				orgTitle: "Rate Limits",
			},
			subscriptions: {
				active: "Active",
				activeSubscribers: "Active subscribers",
				allModelsAccess: "all-models access",
				allTimeDescription:
					"Lifetime totals - unaffected by cycle resets or block/disable",
				avgUtilization: "Avg utilization",
				cancelPending: "Cancel pending",
				cancelling: "{count} cancelling",
				churned: "Churned",
				committed: "{amount} committed",
				committedMrrTooltip:
					"Forward-looking MRR after pending churn. Excludes subs flagged to cancel at period end (still billed by Stripe this cycle, but gone next cycle).",
				costAllTime: "Cost (all-time)",
				cycle: "Cycle",
				cycleDay: "Day {day}",
				cycleDayOfCycle: "Day {day} of cycle",
				cycleMargin: "Cycle margin",
				cycleUtilization: "Cycle utilization",
				chatPlansTitle: "doteb Chat",
				chatPlansDescription:
					"Chat Plan subscribers across Starter, Plus and Pro - current cycle utilization, real provider cost, and margin.",
				devpassDescription:
					"Subscribers across Lite, Pro and Max - current cycle utilization, real provider cost, and margin.",
				expired: "Expired",
				grossMrr: "Gross MRR",
				lite: "Lite",
				marginAllTime: "Margin (all-time)",
				max: "Max",
				mrr: "MRR",
				negativeMarginOnly: "Negative margin only",
				netAfterRefundsThisMonth: "Net after refunds this month: {amount}",
				netNewThisMonth:
					"Net new this month: {count} ({starts} starts / {ends} ends)",
				noSubscribersMatch: "No subscribers match",
				noActiveCycle: "No active cycle",
				noPaymentFailures: "No payment failures",
				noSubscriptionEvents: "No subscription events recorded",
				ok: "ok",
				openInOrganizations: "Open in Organizations",
				other: "Other",
				overCap: "Over cap",
				paymentIssue: "payment issue",
				pro: "Pro",
				profitMarginTitle: "Profit margin: cycle margin / gross MRR",
				profitPct: "{percent}% profit",
				providerCostThisCycle: "{amount} provider cost this cycle",
				realProviderCostAllTime: "Real provider cost (all-time)",
				realProviderCostCycle: "Real provider cost (cycle)",
				realCost: "Real cost",
				projectStatsSource: "From hourly project stats",
				renewsValue: "Renews {date}",
				revenueAllTime: "Revenue (all-time)",
				revenueNetAllCycles: "Net of refunds, across all cycles",
				revenueProviderCost: "Revenue - provider cost",
				refunded: "after {amount} refunded",
				refundsProcessed: "{count} refunds processed",
				refundsThisMonth: "Refunds this month",
				renews: "Renews",
				searchPlaceholder: "Search by org name, email, owner, or ID...",
				showChurned: "Show churned",
				since: "Since",
				subscriber: "Subscriber",
				subscribedAt: "Subscribed {date}",
				subscriptionHistory: "Subscription history",
				subscribersMatch: "{count} subscribers match current filters",
				tier: "Tier",
				tierChangesAllTime: "{count} tier changes all time",
				tierDelta: "Tier Δ",
				tierValue: "Tier: {tier}",
				allTimeMarginTitle: "Revenue {revenue} - cost {cost}",
				utilization: "Utilization",
				usedOfLimit: "{used} of {limit}",
				weightedAcrossActive: "Weighted across active subs",
			},
			supportEmail: {
				additionalContext: "Additional Context (optional)",
				body: "Body",
				draftGenerated: "Draft generated",
				emailDescription: "Send an email to {email} from contact@doteb.com",
				failedGenerate: "Failed to generate reply",
				failedSendEmail: "Failed to send email",
				failedSendReply: "Failed to send reply",
				emailTitle: "Email {recipient}",
				generateAi: "Generate with AI",
				generatingDraft: "Generating draft...",
				messageRequired: "Subject and body are required",
				sendEmail: "Send Email",
				sendReply: "Send Reply",
				sending: "Sending...",
				subject: "Subject",
				welcomeSubjectPlaceholder: "Welcome to doteb",
				replySubjectPlaceholder: "Re: Enterprise inquiry",
				bodyPlaceholder: "Write your email...",
				replyBodyPlaceholder: "Write your reply...",
				contextPlaceholder: "e.g. They're interested in image generation...",
				replyContextPlaceholder: "e.g. They already use OpenAI directly...",
				replyDescription: "Send a reply to {email} from contact@doteb.com",
				replyTitle: "Reply to {name}",
				emailSent: "Email sent successfully",
			},
			supportLogs: {
				admin: "Admin",
				avgRating: "avg rating",
				backToConversations: "Back to conversations",
				bot: "Bot",
				contactDetails: "Contact Details",
				conversations: "Conversations",
				deleteConversationLongDescription:
					"This will permanently delete the conversation and all its messages. This action cannot be undone.",
				deleteConversation: "Delete Conversation",
				deleteConversationDescription:
					"This conversation will be permanently deleted.",
				deleteConversationSr: "Delete conversation",
				device: "Device",
				deviceOnOs: "{browser} on {os}",
				dislikes: "Dislikes",
				escalated: "Escalated",
				failedSendReply: "Failed to send reply. Please try again.",
				ipAddress: "IP Address",
				justNow: "just now",
				likes: "Likes",
				messages: "Messages",
				messagesCount: "{count} messages",
				noConversationsFound: "No conversations found",
				noMessagesConversation: "No messages in this conversation",
				noMessagesYet: "No messages yet",
				replyEmailPlaceholder: "Reply (will also be sent via email)...",
				replyPlaceholder: "Reply...",
				resolved: "Resolved",
				resolvedByVisitor: "Resolved by visitor",
				searchPlaceholder: "Search messages...",
				selectConversation: "Select a conversation to view",
				selectDetails: "Select a conversation to view visitor details",
				sessionInfo: "Session Info",
				sendReplySr: "Send reply",
				started: "Started",
				totalRatings: "total ratings",
				totalConversations: "{count} total conversations",
				visitor: "Visitor",
				visitorDevice: "Visitor Device",
			},
			ui: {
				carousel: "carousel",
				nextSlide: "Next slide",
				previousSlide: "Previous slide",
				sidebar: "Sidebar",
				sidebarDescription: "Displays the mobile sidebar.",
				slide: "slide",
				toggleSidebar: "Toggle Sidebar",
			},
			timeRange: {
				last1Hour: "Last 1 hour",
				last4Hours: "Last 4 hours",
				last12Hours: "Last 12 hours",
				last24Hours: "Last 24 hours",
				last7Days: "Last 7 days",
				last30Days: "Last 30 days",
				last90Days: "Last 90 days",
				last365Days: "Last 365 days",
				selectWindow: "Select window",
			},
			usage: {
				cacheWriteTokensCost: "Cache Write Tokens & Cost",
				cacheWriteTokensCostSubtitle:
					"Tokens and cost paid to write into the cache",
				cachedTokensCost: "Cached Tokens & Cost",
				cachedTokensCostSubtitle:
					"Tokens and cost served from cache (if supported)",
				chatPlansSpendDescription:
					"Chat Plans spend from hourly project rollups.",
				chatPlansSourceSpendDescription:
					"Chat Plans `source` header spend from hourly project rollups.",
				devpassSpendDescription: "DevPass spend from hourly project rollups.",
				devpassSourceSpendDescription:
					"DevPass `source` header spend from hourly project rollups.",
				discountSavingsSubtitle: "Discount savings from applied discounts",
				inputTokensCost: "Input Tokens & Cost",
				inputTokensCostSubtitle: "Prompt tokens and associated cost",
				mostUsedModelByCost: "Most Used Model (by cost)",
				mostUsedProvider: "Most Used Provider",
				loadingUsageData: "Loading usage data...",
				noCodingAgentTraffic: "No coding-agent traffic in the selected range.",
				noModelUsage: "No model usage in the selected range.",
				noProviderUsage: "No provider usage in the selected range.",
				noSourceTraffic: "No source traffic in the selected range.",
				noTraffic: "No traffic in selected window",
				noUsageData: "No usage data available.",
				outputTokensCost: "Output Tokens & Cost",
				outputTokensCostSubtitle: "Completion tokens and associated cost",
				providerForMostExpensiveModel: "Provider for the most expensive model",
				requestsSubtitle: "All API requests in the selected time window",
				totalSavings: "Total Savings",
				totalCostSubtitle: "Sum of metered usage costs (USD)",
				totalCostValue: "{cost} total cost",
				totalTokensWindow: "Total tokens across all requests ({window})",
				topCodingAgents: "Top coding agents",
				topModels: "Top models",
				topProviders: "Top providers",
				topSources: "Top sources",
				usageMetrics: "Usage Metrics",
			},
			signInPrompt: {
				description: "Sign in to access the admin dashboard",
				title: "Admin Dashboard",
			},
		},
	},
	"zh-CN": {
		admin: {
			auth: {
				emailInvalid: "请输入有效的邮箱地址",
				loginSuccessful: "登录成功",
				or: "或",
				passwordMin: "密码至少需要 6 个字符",
				passwordRequired: "请输入密码",
				signingIn: "正在登录...",
				subtitle: "输入邮箱和密码登录你的管理员账号",
				unknownError: "发生未知错误",
				welcomeBack: "欢迎回来",
			},
			common: {
				actions: "操作",
				all: "全部",
				allModels: "全部模型",
				allProviders: "全部供应商",
				allTime: "累计",
				amount: "金额",
				anonymous: "匿名",
				archive: "归档",
				archived: "已归档",
				avgTtft: "平均 TTFT",
				avgDuration: "平均耗时",
				backToOrganization: "返回组织",
				baseUrl: "Base URL",
				cached: "缓存",
				cacheWrite: "缓存写入",
				cancel: "取消",
				client: "客户端",
				completed: "已完成",
				contentFilter: "内容过滤",
				context: "上下文",
				cost: "成本",
				country: "国家/地区",
				created: "创建时间",
				credits: "额度",
				date: "日期",
				delete: "删除",
				deleting: "正在删除...",
				details: "详情",
				disabled: "已禁用",
				discount: "折扣",
				description: "描述",
				email: "邮箱",
				errorRate: "错误率",
				errors: "错误",
				event: "事件",
				expires: "过期时间",
				family: "模型族",
				failed: "失败",
				free: "免费",
				gateway: "网关",
				failedCopyId: "复制 ID 失败",
				hide: "收起",
				history: "历史",
				id: "ID",
				idCopied: "ID 已复制",
				inputPrice: "输入价格",
				inputTokens: "输入 Token",
				joined: "加入时间",
				lastUpdated: "最后更新",
				lengthLimit: "长度限制",
				limit: "限制",
				loading: "加载中...",
				maxOutput: "最大输出",
				message: "消息",
				model: "模型",
				models: "模型",
				name: "名称",
				never: "永不过期",
				noData: "暂无数据。",
				notProvided: "未提供",
				none: "无",
				organization: "组织",
				outputPrice: "输出价格",
				outputTokens: "输出 Token",
				pageOf: "第 {page} 页，共 {totalPages} 页",
				payments: "支付",
				plan: "套餐",
				premiumWeek: "Premium（周）",
				project: "项目",
				previous: "上一页",
				next: "下一页",
				pricing: "价格",
				provider: "供应商",
				providers: "供应商",
				reason: "原因",
				region: "区域",
				requests: "请求",
				role: "角色",
				save: "保存",
				search: "搜索",
				showing: "显示第 {from} 到 {to} 条，共 {total} 条",
				size: "规模",
				source: "来源",
				status: "状态",
				streaming: "流式",
				token: "Token",
				tokens: "Token",
				toolCalls: "工具调用",
				totalCost: "总成本",
				totalRequests: "总请求数",
				totalTokens: "总 Token 数",
				type: "类型",
				unknown: "未知",
				unarchive: "取消归档",
				upstream: "上游",
				usage: "用量",
				userAgent: "User Agent",
				value: "值",
				verified: "已验证",
				unverified: "未验证",
			},
			dashboard: {
				audience: "用户",
				creditFlow: "额度流转",
				description: "用户、客户和收入概览。",
				fees: "费用",
				gifted: "赠送",
				allTimeCreditsPurchased: "累计购买额度",
				liveData: "实时数据",
				netRevenueSubtitle: "净收入（不含 Stripe 费用和退款）",
				orgs: "组织",
				overage: "超额：{amount}",
				overageDescription: "支出超过已充值额度。",
				paying: "付费",
				processed: "已处理",
				refunds: "退款",
				revenue: "收入",
				spent: "已用",
				title: "管理后台",
				totalSignups: "累计注册",
				unused: "未用",
				verified: "已验证",
				viewOrganizations: "查看组织",
			},
			globalStats: {
				allDimensionAcrossRange: "{range} 内全部 {count} 个{dimension}。",
				breakdown: "拆分",
				byModel: "按模型",
				bySource: "按 x-source",
				canonical: "标准模型",
				canonicalModel: "标准模型",
				costBreakdown: "输入：{input} · 输出：{output}",
				dailyTimeseries: "每日时间序列",
				description: "跨组织用量按天聚合，可按模型或 x-source header 分组。",
				distinctDimension: "不同{dimension}数",
				errorsCached: "错误：{errors} · 缓存：{cached}",
				failedToLoad: "指标加载失败。",
				mapping: "映射",
				mappings: "映射",
				metricShare: "{metric}占比 - {dimension}",
				noDataForRange: "该范围暂无数据。",
				ofTotal: "共 {total}",
				otherCount: "其他（{count}）",
				pageSlash: "第 {page} / {totalPages} 页",
				selectedRange: "所选范围",
				sources: "来源",
				timeseriesAggregateDescription:
					"按天聚合 {metric}，覆盖全部{dimensions}。",
				timeseriesBreakdownDescription:
					"按天聚合 {metric}，并按{dimension}拆分。",
				title: "全局指标",
				tokensBreakdown: "输入：{input} · 缓存：{cached} · 输出：{output}",
				topOtherAcrossRange: "{range} 内前 10 项 + 其他。",
			},
			history: {
				aggregatedProviderRequestVolumeDescription:
					"聚合全部供应商的请求量、错误、延迟和 Token 时间趋势",
				avgDurationMs: "平均耗时（ms）",
				avgTtftMs: "平均 TTFT（ms）",
				costUsd: "成本（$）",
				noProvidersServeModel: "没有供应商服务该模型",
				perProviderHistory: "按供应商查看历史",
				projectRequestVolumeDescription:
					"该项目每小时请求量、错误、Token 和成本",
				requestVolumeForProvider: "{provider} 的请求量、错误、延迟和 Token",
				requestVolumeDescription: "请求量、错误、延迟和 Token 的时间趋势",
				tokensPerSecond: "Token/秒",
			},
			language: {
				label: "语言",
			},
			nav: {
				chatPlans: "Chat 订阅",
				chatSupportLogs: "Chat 支持日志",
				contactSubmissions: "联系表单",
				dashboard: "仪表盘",
				devpass: "DevPass",
				globalDiscounts: "全局折扣",
				globalRateLimits: "全局限流",
				globalStats: "全局指标",
				main: "主要功能",
				modelMappings: "模型映射",
				models: "模型",
				organizations: "组织",
				paymentFailures: "支付失败",
				providers: "供应商",
				toggleMenu: "展开菜单",
			},
			organizations: {
				apiKeys: "API Keys",
				allTimeCredits: "累计额度",
				back: "返回",
				blockAccount: "封禁账号",
				blockAccountConfirm: "确认封禁账号",
				blockAccountDescription: "你将封禁 {orgName}。这会：",
				blockAccountFailed: "组织封禁失败",
				blockAccountSubscriptionEffect:
					"立即取消该组织所有活跃的 Stripe 订阅（DevPass 和所有 Pro 订阅）。",
				blockAccountGatewayEffect: "将组织标记为已删除，网关请求会被拒绝。",
				blockAccountMembersEffect: "停用所有成员，并让他们退出所有会话。",
				blockAccountReminder:
					"此操作仅用于确认滥用（重复卡、共享 key、欺诈）。如果误操作，可通过状态切换重新启用。",
				blockAccountTitle: "封禁这个账号？",
				collapseIamRules: "收起 IAM 规则",
				createdAt: "创建时间：{date}",
				creditsValue: "额度：{credits}",
				devPlan: "开发套餐",
				devPlanValue: "开发：{plan}",
				disableOrganization: "禁用组织",
				disableOrganizationConfirm:
					"确定要禁用组织“{orgName}”吗？所有成员将被停用并登出，网关请求会返回 HTTP 410。",
				expandIamRules: "展开 IAM 规则",
				giftCredits: "赠送额度",
				giftCreditsAmount: "额度金额",
				giftCreditsComment: "备注（可选）",
				giftCreditsCommentHelper: "会保存在交易描述中",
				giftCreditsCommentPlaceholder: "例如：欢迎奖励、宕机补偿",
				giftCreditsDescription:
					"向 {orgName} 赠送额度。这会创建一条交易记录并更新组织额度余额。",
				giftCreditsFailed: "赠送额度失败",
				giftCreditsInvalid: "额度金额必须是正数",
				giftCreditsPlaceholder: "例如：50",
				iamRules: "IAM 规则",
				manageDiscounts: "管理折扣",
				manageRateLimits: "管理限流",
				members: "成员",
				noApiKeysFound: "未找到 API Key",
				noMembersFound: "未找到成员",
				noProviderKeysFound: "未找到供应商 Key",
				noTransactionsFound: "未找到交易",
				noResults: "未找到组织",
				providerKeys: "供应商 Key",
				reEnableOrganization: "重新启用组织",
				reEnableOrganizationConfirm:
					"确定要重新启用组织“{orgName}”吗？成员会被重新激活（除非他们也属于其他被禁用的组织），网关请求将恢复。",
				referralBonus: "推荐奖励",
				referralBonusDescription:
					"启用后，由 {orgName} 推荐的组织在首次充值额度时会获得奖励。",
				referralBonusEnable: "启用奖励",
				referralBonusEnableHelper:
					"被推荐用户只有通过该组织的推荐链接注册时才会获得奖励。",
				referralBonusFailed: "更新推荐奖励失败",
				referralBonusPercent: "奖励百分比",
				referralBonusPercentHelper:
					"添加到被推荐组织首次充值上的百分比（例如 50 = 额外 +50% 额度）。",
				referralBonusPercentInvalid: "百分比必须是 0 到 1000 之间的数字",
				referralBonusTitle: "推荐注册奖励",
				searchPlaceholder: "按名称、邮箱、成员邮箱或 ID 搜索...",
				subtitle: "找到 {count} 个组织 - 总额度：{credits}",
				title: "组织",
				transactions: "交易",
				totalSpent: "总支出",
			},
			paymentFailures: {
				amount: "金额",
				declineCode: "拒付代码",
				days: "天数",
				description: "最近 {days} 天的 Stripe 支付拒付和失败记录",
				failedToLoad: "支付失败记录加载失败。",
				last7Days: "最近 7 天",
				last30Days: "最近 30 天",
				message: "消息",
				noResults: "未找到支付失败记录。",
				searchPlaceholder: "按邮箱搜索...",
				source: "来源",
				stripe: "Stripe",
				time: "时间",
				title: "支付失败",
				topDeclineCodes: "主要拒付代码（30 天）",
				truncated:
					"当前显示 100 条，共 {total} 条。缩小搜索或缩短日期范围可查看更多。",
				view: "查看",
			},
			charts: {
				costByModel: "按模型查看成本",
				costByModelDescription: "所有组织成本最高的 20 个模型",
				costByModelOrgDescription: "该组织成本最高的 20 个模型",
				costByModelOverTime: "模型成本趋势",
				costByModelOverTimeOrgDescription:
					"所选时间窗口内前 10 个模型的堆叠拆分",
				costByModelOverTimeProjectDescription: "该项目前 10 个模型的堆叠拆分",
				devpassRevenueUsage: "DevPass 收入与用量",
				chatPlansRevenueUsage: "聊天订阅收入与用量",
				devpassTimeseriesDescription:
					"DevPass 交易的每日收入、当前和历史订阅者产生的真实供应商成本，以及对应利润。合计值按所选日期范围聚合，可能与当前周期 KPI 卡片不一致。",
				chatPlansTimeseriesDescription:
					"聊天订阅交易的每日收入、当前和历史订阅者产生的真实供应商成本，以及对应利润。合计值按所选日期范围聚合，可能与当前周期 KPI 卡片不一致。",
				chatPlansTimeseriesFailed: "聊天订阅趋势加载失败。",
				providerCost: "供应商成本",
				margin: "利润",
				net: "净额",
				noDataForWindow: "该时间窗口暂无数据",
				paidCustomers: "付费客户",
				revenueDescription:
					"累计处理金额、扣费后收入，以及扣除费用和退款后的净额",
				signups: "注册",
				signupsDescription: "累计注册和付费客户的时间趋势",
				signupsPaidCustomers: "注册与付费客户",
				totalRevenue: "总收入",
			},
			contactSubmissions: {
				archive: "归档",
				back: "返回联系表单",
				companySize: "公司规模",
				delete: "删除表单",
				deleteDescription: "该表单会被归档，并从默认列表中隐藏。",
				deletePermanentDescription: "该联系表单将被永久删除，此操作无法撤销。",
				details: "表单详情",
				failedLoad: "表单加载失败，请稍后重试。",
				found: "找到 {count} 条表单",
				generateReply: "用 AI 生成回复",
				noResults: "未找到表单",
				notFound: "未找到表单。",
				rejectionReason: "拒绝原因",
				replySent: "回复发送成功",
				searchPlaceholder: "按姓名、邮箱或消息搜索...",
				statusDelivered: "已送达",
				statusFailed: "失败",
				statusPending: "待处理",
				statusRejected: "已拒绝",
				submittedAt: "提交时间：{date}",
				title: "联系提交",
				unarchive: "取消归档",
			},
			dateRange: {
				allTime: "全部时间",
				apply: "应用",
				custom: "自定义",
				customRange: "自定义范围...",
				end: "结束",
				from: "开始",
				last7Days: "最近 7 天",
				last30Days: "最近 30 天",
				last3Months: "最近 3 个月",
				last6Months: "最近 6 个月",
				last90Days: "最近 90 天",
				last12Months: "最近 12 个月",
				lastMonth: "上个月",
				lastWeek: "上周",
				lastYear: "去年",
				lastQuarter: "上个季度（{quarter}）",
				start: "开始",
				selectEndMonth: "选择结束月份",
				thisMonth: "本月",
				thisQuarter: "本季度（{quarter}）",
				thisWeek: "本周",
				thisYear: "今年",
				to: "结束",
				today: "今天",
				twoQuartersAgo: "前两个季度（{quarter}）",
				threeQuartersAgo: "前三个季度（{quarter}）",
				backToPresets: "返回预设",
			},
			detailStats: {
				canceled: "已取消",
				clientErrors: "客户端错误",
				gatewayErrors: "网关错误",
				upstreamErrors: "上游错误",
			},
			discounts: {
				add: "添加折扣",
				backToOrganization: "返回组织",
				confirmDelete: "确定要删除这个折扣吗？",
				create: "创建折扣",
				createDescription: "为供应商、模型或其组合创建新的折扣。",
				customerPays: "客户支付原价的 {percent}%",
				description: "适用于所有组织的折扣",
				expiresAtOptional: "过期时间（可选）",
				failedCreate: "折扣创建失败",
				failedCreateDuplicate: "折扣创建失败，可能已经存在。",
				failedCreateGeneric: "创建折扣时发生错误",
				howGlobalWorks: "全局折扣如何工作",
				howOrgWorks: "折扣如何工作",
				howWorksApplied: "折扣会自动应用到 API 用量",
				howWorksAutoOrg: "折扣会自动应用到该组织的所有 API 请求",
				howWorksCustomerPays: "30% 折扣表示客户支付原价的 70%",
				howWorksGlobalAll: "全局折扣适用于所有组织",
				howWorksGlobalOverride:
					"全局折扣会应用到所有组织，除非存在组织级折扣覆盖",
				howWorksOrgPrecedence: "组织级折扣优先于全局折扣",
				howWorksPrecedence:
					"更具体的折扣（供应商 + 模型）优先级高于更宽泛的折扣",
				neverExpiresHelper: "留空表示折扣永不过期",
				noGlobalResults: "还没有配置全局折扣",
				noGlobalResultsDescription: "全局折扣适用于所有组织",
				noOrgResults: "该组织还没有配置折扣",
				noOrgResultsDescription: "添加折扣，为该组织提供特殊价格",
				off: "{percent} 折扣",
				percentLabel: "折扣百分比",
				percentInvalid: "折扣必须在 0 到 100 之间",
				percentPlaceholder: "例如：30 表示 7 折",
				reasonOptional: "原因（可选）",
				reasonPlaceholder: "例如：企业合作伙伴折扣",
				selectProviderOrModel: "请至少选择一个供应商或模型",
				title: "全局折扣",
				orgTitle: "折扣",
			},
			models: {
				noResults: "未找到模型",
				perProviderHistory: "按供应商查看历史",
				providerCount: "{count} 个供应商",
				providersCount: "{count} 个供应商",
				searchPlaceholder: "按名称或 ID 搜索...",
				subtitle: "找到 {count} 个模型 - 点击行查看详情",
				title: "模型",
			},
			modelMappings: {
				bucketedHourly: "项目数据按小时分桶",
				projectSubtitle: "{projectName} 有 {count} 个模型-供应商组合",
				backToProject: "返回项目",
				noResults: "未找到映射",
				searchPlaceholder: "按模型或供应商搜索...",
				subtitle: "共 {count} 个映射 - 每个供应商可用的所有模型",
				title: "模型-供应商映射",
			},
			providers: {
				noModelsServed: "该供应商还没有服务任何模型",
				noResults: "未找到供应商",
				subtitle: "共 {count} 个供应商 - 点击行查看历史",
				title: "供应商",
			},
			projectLogs: {
				allProviders: "全部供应商",
				allSources: "全部来源",
				allStatuses: "全部状态",
				filterByModel: "按模型过滤",
				filterByProvider: "按供应商过滤",
				filterBySource: "按来源过滤",
				filterByStatus: "按状态过滤",
				loadMore: "加载更多",
				loadingLogs: "正在加载日志...",
				noLogs: "该项目没有日志。",
				noModels: "未找到模型。",
				recentLogs: "最近日志",
				refresh: "刷新",
				refreshing: "正在刷新...",
				searchModels: "搜索模型...",
			},
			projects: {
				cachingEnabled: "已启用缓存",
				cached: "已缓存",
				modelProviderMappings: "模型-供应商映射",
				projects: "项目",
			},
			rateLimits: {
				add: "添加限流",
				confirmDelete: "确定要删除这个限流规则吗？",
				create: "创建限流",
				description: "适用于所有组织的 RPM 上限",
				dialogDescription:
					"为供应商、模型或其组合设置每分钟或每天的最大请求数。",
				enforcement: "执行方式",
				enforcementGlobal: "全局共享",
				enforcementGlobalDescription: "所有组织共享同一个计数器",
				enforcementGlobalLong: "全局共享（所有组织共享）",
				enforcementPerOrg: "按组织",
				enforcementPerOrgDescription: "每个组织都有自己的计数器",
				failedCreate: "限流创建失败",
				failedCreateDuplicate: "限流创建失败，可能已经存在。",
				failedCreateGeneric: "创建限流时发生错误",
				howItWorks: "全局限流如何工作",
				howItWorksAll: "全局限流会应用到所有组织，除非存在组织级覆盖规则",
				howItWorksCaps: "上限可以定义为每分钟请求数（RPM）或每天请求数（RPD）",
				howItWorksFallback:
					"达到上限时，网关会优先选择其他可用供应商，然后才返回 429",
				howItWorksGlobal:
					"全局共享执行方式在所有组织之间共用一个计数器（例如全平台总共 10 RPM）",
				howItWorksPerOrg:
					"按组织执行方式会让每个组织独立计数（例如每个组织 10 RPM）",
				howItWorksSpecific:
					"更具体的限流（供应商 + 模型）优先级高于更宽泛的规则",
				limitType: "限制类型",
				maxLimit: "最大 {limitType}",
				maxLimitInvalid: "最大 {limitType} 必须是正整数",
				maxRpdDescription: "允许的每天最大请求数",
				maxRpmDescription: "允许的每分钟最大请求数",
				modelHelper: "正在显示 {provider} 可用的模型",
				noResults: "还没有配置全局限流",
				noResultsDescription:
					"全局限流会限制指定供应商或模型的每分钟/每天最大请求数",
				reason: "原因",
				reasonOptional: "原因（可选）",
				reasonPlaceholder: "例如：防止昂贵模型被滥用",
				selectProviderOrModel: "请至少选择一个供应商或模型",
				title: "全局限流",
				orgAddDescription: "为该组织添加 RPM 或 RPD 上限",
				orgHowItWorks: "限流如何工作",
				orgHowWorksFallback:
					"达到限流时，网关会尽可能回退到其他供应商；否则返回 429 Too Many Requests",
				orgHowWorksPrecedence: "组织级限流优先于全局限流",
				orgHowWorksScope:
					"限流会限制匹配供应商和模型的每分钟请求数（RPM）或每天请求数（RPD）",
				orgNoResults: "该组织还没有配置限流",
				orgTitle: "限流",
			},
			subscriptions: {
				active: "活跃",
				activeSubscribers: "活跃订阅者",
				allModelsAccess: "全模型访问",
				allTimeDescription: "生命周期合计 - 不受周期重置或封禁/禁用影响",
				avgUtilization: "平均利用率",
				cancelPending: "待取消",
				cancelling: "{count} 个待取消",
				churned: "已流失",
				committed: "{amount} 已承诺",
				committedMrrTooltip:
					"扣除待流失后的前瞻性 MRR。不包含已标记周期末取消的订阅（本周期 Stripe 仍会计费，但下周期会消失）。",
				costAllTime: "累计成本",
				cycle: "周期",
				cycleDay: "第 {day} 天",
				cycleDayOfCycle: "周期第 {day} 天",
				cycleMargin: "周期利润",
				cycleUtilization: "周期利用率",
				chatPlansTitle: "Doteb Chat 订阅",
				chatPlansDescription:
					"Starter、Plus 和 Pro 聊天订阅用户 - 当前周期利用率、真实供应商成本和利润。",
				devpassDescription:
					"Lite、Pro 和 Max 订阅者 - 当前周期利用率、真实供应商成本和利润。",
				expired: "已过期",
				grossMrr: "总 MRR",
				lite: "Lite",
				marginAllTime: "累计利润",
				max: "Max",
				mrr: "MRR",
				negativeMarginOnly: "仅负利润",
				netAfterRefundsThisMonth: "本月扣除退款后净额：{amount}",
				netNewThisMonth:
					"本月净新增：{count}（{starts} 个开始 / {ends} 个结束）",
				noSubscribersMatch: "没有匹配的订阅者",
				noActiveCycle: "无活跃周期",
				noPaymentFailures: "无支付失败记录",
				noSubscriptionEvents: "没有订阅事件记录",
				ok: "正常",
				openInOrganizations: "在组织中打开",
				other: "其他",
				overCap: "超出上限",
				paymentIssue: "支付问题",
				pro: "Pro",
				profitMarginTitle: "利润率：周期利润 / 总 MRR",
				profitPct: "{percent}% 利润",
				providerCostThisCycle: "{amount} 本周期供应商成本",
				realProviderCostAllTime: "真实供应商成本（累计）",
				realProviderCostCycle: "真实供应商成本（周期）",
				realCost: "真实成本",
				projectStatsSource: "来自每小时项目统计",
				renewsValue: "续费时间：{date}",
				revenueAllTime: "收入（累计）",
				revenueNetAllCycles: "扣除退款后的跨周期净额",
				revenueProviderCost: "收入 - 供应商成本",
				refunded: "已退款 {amount}",
				refundsProcessed: "已处理 {count} 笔退款",
				refundsThisMonth: "本月退款",
				renews: "续费",
				searchPlaceholder: "按组织名称、邮箱、负责人或 ID 搜索...",
				showChurned: "显示流失",
				since: "开始时间",
				subscriber: "订阅者",
				subscribedAt: "订阅时间：{date}",
				subscribersMatch: "{count} 个订阅者匹配当前筛选",
				subscriptionHistory: "订阅历史",
				tier: "档位",
				tierChangesAllTime: "累计 {count} 次档位变更",
				tierDelta: "档位变更",
				tierValue: "档位：{tier}",
				allTimeMarginTitle: "收入 {revenue} - 成本 {cost}",
				utilization: "利用率",
				usedOfLimit: "{used} / {limit}",
				weightedAcrossActive: "按活跃订阅加权",
			},
			supportEmail: {
				additionalContext: "补充上下文（可选）",
				body: "正文",
				draftGenerated: "草稿已生成",
				emailDescription: "从 contact@doteb.com 向 {email} 发送邮件",
				failedGenerate: "生成回复失败",
				failedSendEmail: "邮件发送失败",
				failedSendReply: "回复发送失败",
				emailTitle: "给 {recipient} 发邮件",
				generateAi: "用 AI 生成",
				generatingDraft: "正在生成草稿...",
				messageRequired: "请填写主题和正文",
				sendEmail: "发送邮件",
				sendReply: "发送回复",
				sending: "正在发送...",
				subject: "主题",
				welcomeSubjectPlaceholder: "欢迎使用 doteb",
				replySubjectPlaceholder: "Re: 企业咨询",
				bodyPlaceholder: "编写邮件...",
				replyBodyPlaceholder: "编写回复...",
				contextPlaceholder: "例如：对图像生成感兴趣...",
				replyContextPlaceholder: "例如：已经直接使用 OpenAI...",
				replyDescription: "从 contact@doteb.com 向 {email} 发送回复",
				replyTitle: "回复 {name}",
				emailSent: "邮件发送成功",
			},
			supportLogs: {
				admin: "管理员",
				avgRating: "平均评分",
				backToConversations: "返回会话列表",
				bot: "Bot",
				contactDetails: "联系方式",
				conversations: "会话",
				deleteConversationLongDescription:
					"这会永久删除该会话及其中所有消息。此操作无法撤销。",
				deleteConversation: "删除会话",
				deleteConversationDescription: "该会话将被永久删除。",
				deleteConversationSr: "删除会话",
				device: "设备",
				deviceOnOs: "{browser} / {os}",
				dislikes: "点踩",
				escalated: "已升级",
				failedSendReply: "回复发送失败，请重试。",
				ipAddress: "IP 地址",
				justNow: "刚刚",
				likes: "点赞",
				messages: "消息",
				messagesCount: "{count} 条消息",
				noConversationsFound: "未找到会话",
				noMessagesConversation: "该会话暂无消息",
				noMessagesYet: "暂无消息",
				replyEmailPlaceholder: "回复（也会通过邮件发送）...",
				replyPlaceholder: "回复...",
				resolved: "已解决",
				resolvedByVisitor: "访客已解决",
				searchPlaceholder: "搜索消息...",
				selectConversation: "选择一个会话查看",
				selectDetails: "选择一个会话查看访客详情",
				sessionInfo: "会话信息",
				sendReplySr: "发送回复",
				started: "开始时间",
				totalRatings: "总评分数",
				totalConversations: "共 {count} 个会话",
				visitor: "访客",
				visitorDevice: "访客设备",
			},
			ui: {
				carousel: "轮播",
				nextSlide: "下一张幻灯片",
				previousSlide: "上一张幻灯片",
				sidebar: "侧边栏",
				sidebarDescription: "显示移动端侧边栏。",
				slide: "幻灯片",
				toggleSidebar: "切换侧边栏",
			},
			timeRange: {
				last1Hour: "最近 1 小时",
				last4Hours: "最近 4 小时",
				last12Hours: "最近 12 小时",
				last24Hours: "最近 24 小时",
				last7Days: "最近 7 天",
				last30Days: "最近 30 天",
				last90Days: "最近 90 天",
				last365Days: "最近 365 天",
				selectWindow: "选择时间窗口",
			},
			usage: {
				cacheWriteTokensCost: "缓存写入 Token 与成本",
				cacheWriteTokensCostSubtitle: "写入缓存产生的 Token 和成本",
				cachedTokensCost: "缓存 Token 与成本",
				cachedTokensCostSubtitle: "从缓存提供的 Token 和成本（如支持）",
				chatPlansSpendDescription: "聊天订阅每小时项目汇总支出。",
				chatPlansSourceSpendDescription:
					"聊天订阅 `source` header 每小时项目汇总支出。",
				devpassSpendDescription: "DevPass 每小时项目汇总支出。",
				devpassSourceSpendDescription:
					"DevPass `source` header 每小时项目汇总支出。",
				discountSavingsSubtitle: "已应用折扣带来的节省",
				inputTokensCost: "输入 Token 与成本",
				inputTokensCostSubtitle: "Prompt Token 及相关成本",
				mostUsedModelByCost: "最常用模型（按成本）",
				mostUsedProvider: "最常用供应商",
				loadingUsageData: "正在加载用量数据...",
				noCodingAgentTraffic: "所选范围内没有 coding-agent 流量。",
				noModelUsage: "所选范围内没有模型用量。",
				noProviderUsage: "所选范围内没有供应商用量。",
				noSourceTraffic: "所选范围内没有来源流量。",
				noTraffic: "所选窗口内无流量",
				noUsageData: "暂无用量数据。",
				outputTokensCost: "输出 Token 与成本",
				outputTokensCostSubtitle: "Completion Token 及相关成本",
				providerForMostExpensiveModel: "成本最高模型对应的供应商",
				requestsSubtitle: "所选时间窗口内的全部 API 请求",
				totalSavings: "总节省",
				totalCostSubtitle: "计费用量成本合计（USD）",
				totalCostValue: "总成本 {cost}",
				totalTokensWindow: "所有请求的总 Token（{window}）",
				topCodingAgents: "主要 coding agent",
				topModels: "主要模型",
				topProviders: "主要供应商",
				topSources: "主要来源",
				usageMetrics: "用量指标",
			},
			signInPrompt: {
				description: "登录后访问管理后台",
				title: "管理后台",
			},
		},
	},
} satisfies Partial<Record<Locale, TranslationTree>>;
